import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { authSessionUserSchema, type AuthSessionUser } from '@aia/platform-core';

import type { EnabledModules } from '@/hooks/useModules';

import { api, authApi, getApiError } from './api';
import { setAuthenticatedUser, clearAuthenticatedUser } from './telemetry';

export type User = Omit<AuthSessionUser, 'enabledModules'> & {
  enabledModules?: EnabledModules;
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    turnstileToken?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; error?: string }>;
  persistAuth: (token: string, user: User) => void;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  organizationId?: string;
  role?: string;
  isPlatformAdmin?: boolean;
  setupKey?: string;
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    return (payload.exp ?? 0) * 1000 < Date.now();
  } catch {
    return true;
  }
}

// Storage keys
const TOKEN_KEY = 'pilotpulse_token';
const USER_KEY = 'pilotpulse_user';

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from storage (localStorage fallback supports Playwright storageState)
  useEffect(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      sessionStorage.setItem(TOKEN_KEY, storedToken);
      sessionStorage.setItem(USER_KEY, storedUser);
      setToken(storedToken);
      try {
        const parsed: unknown = JSON.parse(storedUser) as unknown;
        const result = authSessionUserSchema.safeParse(parsed);
        if (!result.success) {
          clearAuth();
          setIsLoading(false);
          return;
        }
        setUser(result.data as User);
      } catch {
        clearAuth();
        setIsLoading(false);
        return;
      }

      void verifyToken();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verify token with backend
  const verifyToken = async () => {
    try {
      const response = await authApi.get<User>('/auth/me');
      setUser(response.data);
    } catch {
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  // Periodic JWT expiry check
  useEffect(() => {
    const checkExpiry = () => {
      const t = sessionStorage.getItem(TOKEN_KEY);
      if (t && isTokenExpired(t)) {
        clearAuth();
      }
    };

    const interval = setInterval(checkExpiry, 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear auth state
  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    clearAuthenticatedUser();
  }, []);

  // Login
  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    try {
      const response = await authApi.post<{ user: User; token: string }>('/auth/login', {
        email,
        password,
        turnstileToken,
      });

      const newUser = response.data.user;
      const newToken = response.data.token;

      sessionStorage.setItem(TOKEN_KEY, newToken);
      sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);
      setAuthenticatedUser(newUser.id, newUser.organizationId ?? undefined);

      return { success: true };
    } catch (error) {
      return { success: false, error: getApiError(error) };
    }
  }, []);

  // Register
  const register = useCallback(async (data: RegisterData) => {
    try {
      const response = await authApi.post<{ user: User; token: string }>('/auth/register', data);

      const newUser = response.data.user;
      const newToken = response.data.token;

      sessionStorage.setItem(TOKEN_KEY, newToken);
      sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);

      return { success: true };
    } catch (error) {
      return { success: false, error: getApiError(error) };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    void api.post('/auth/logout').catch(() => {
      // logout error is non-critical
    });
    clearAuth();
  }, [clearAuth]);

  // Forgot password
  const forgotPassword = useCallback(async (email: string) => {
    try {
      await authApi.post('/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: getApiError(error) };
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (resetToken: string, password: string) => {
    try {
      await authApi.post('/auth/reset-password', {
        token: resetToken,
        password,
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: getApiError(error) };
    }
  }, []);

  const persistAuth = useCallback((newToken: string, newUser: User) => {
    sessionStorage.setItem(TOKEN_KEY, newToken);
    sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
    setAuthenticatedUser(newUser.id, newUser.organizationId ?? undefined);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    persistAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
